/* eslint-disable max-lines */
import axios, { AxiosRequestConfig } from "axios";
import { API_URL, ENV } from "../config";
import {
  z,
  ZodArray,
  ZodBoolean,
  ZodDefault,
  ZodDiscriminatedUnion,
  ZodNullable,
  ZodObject,
  ZodRecord,
  ZodTypeAny,
  ZodUnion,
} from "zod";
import { duplicateCallTracker } from "./duplicateCallTracker";
import * as Sentry from "@sentry/react";

// Type for accepted Zod schemas in API methods
type AcceptedZodSchema =
  | ZodObject<z.ZodRawShape>
  | ZodArray<ZodTypeAny>
  | ZodUnion<[ZodTypeAny, ...ZodTypeAny[]]>
  | ZodDiscriminatedUnion<string, [ZodObject<z.ZodRawShape>, ...ZodObject<z.ZodRawShape>[]]>
  | ZodRecord<z.ZodString, ZodTypeAny>
  | ZodBoolean;

axios.defaults.baseURL = API_URL;
axios.defaults.headers.post["Content-Type"] = "application/json";

const streamHeaders: Record<string, string> = {};

// intercepting to capture errors
axios.interceptors.response.use(
  function (response) {
    return response.data !== undefined ? response.data : response;
  },
  function (error) {
    // Any status codes that falls outside the range of 2xx cause this function to trigger
    let message;
    const status = error.response?.status;
    switch (status) {
      case 500:
        message = error.response?.data?.detail || "Internal Server Error";
        break;
      case 401:
        message = error?.response?.data?.detail || "Invalid credentials";
        break;
      case 404:
        message = `Sorry! the data you are looking for could not be found. ${
          error.response?.data?.detail ? `Details: ${error.response?.data?.detail}` : ""
        }`;
        break;
      default:
        message = error.response?.data?.detail || error?.message || error;
    }

    // Send API errors to Rollbar if enabled
    if (window.Rollbar && status && status >= 500) {
      window.Rollbar.error("API Error", {
        url: error.config?.url,
        method: error.config?.method,
        status: status,
        statusText: error.response?.statusText,
        message: message,
        response: error.response?.data,
      });
    }

    return Promise.reject(message);
  }
);

const setUserGroupHeader = (userGroupId?: string) => {
  console.log("Setting team header...", userGroupId);
  if (userGroupId && userGroupId !== "-1") {
    axios.defaults.headers.common["x-user-group-id"] = userGroupId;
    streamHeaders["x-user-group-id"] = userGroupId;
  } else {
    delete axios.defaults.headers.common["x-user-group-id"];
    delete streamHeaders["x-user-group-id"];
  }
};

// Initialize the team header from localStorage at module load so the VERY FIRST
// request is RBAC-scoped to the selected team. UserGroupSelector also sets it
// reactively, but that runs only after the team list finishes loading, which
// races with early data fetches (and a stale/absent header leaks another team's
// data until the refetch). The key matches UserGroupSelector's LocalStorageKey.
try {
  setUserGroupHeader(localStorage.getItem("userGroupId") ?? undefined);
} catch {
  // localStorage unavailable (SSR / privacy mode) — header stays unset.
}

const setAuthorizationHeader = (authToken: string) => {
  console.log("Setting authorization header...");
  if (authToken) {
    axios.defaults.headers.common["Authorization"] = "Bearer " + authToken;
    streamHeaders["Authorization"] = "Bearer " + authToken;
  } else {
    delete axios.defaults.headers.common["Authorization"];
    delete streamHeaders["Authorization"];
  }
};

const setTenantHeader = (tenant: string) => {
  console.log("Setting tenant...");
  if (tenant) {
    axios.defaults.headers.common["x-tenant"] = tenant;
    streamHeaders["x-tenant"] = tenant;
  } else {
    delete axios.defaults.headers.common["x-tenant"];
    delete streamHeaders["x-tenant"];
  }
};

const checkSecureConnection = () => {
  if (ENV === "staging" || ENV === "production") {
    if (window.location.protocol !== "https:" || API_URL?.substring(0, 5) !== "https") {
      return false;
    }
  }
  return true;
};

const applyCatchToSchema = <T extends ZodTypeAny>(schema: T, defaultValue = "test"): T => {
  if (schema instanceof ZodObject) {
    const shape = schema.shape; // Access the shape correctly
    const newShape = Object.fromEntries(
      Object.entries(shape).map(([key, value]) => [
        key,
        applyCatchToSchema(value as ZodTypeAny, defaultValue),
      ])
    );
    return new ZodObject({
      ...schema._def,
      shape: () => newShape,
    }) as unknown as T;
  } else if (schema instanceof ZodArray) {
    return new ZodArray({
      ...schema._def,
      type: applyCatchToSchema(schema._def.type, defaultValue),
    }) as unknown as T;
  } else if (schema instanceof ZodNullable || schema instanceof ZodDefault) {
    return schema.catch("") as unknown as T;
  } else if (schema._def.typeName === "ZodString" || schema._def.typeName === "ZodNumber") {
    schema._def.defaultValue = undefined;
    // Applies .catch to string and number schemas
    return schema.catch("") as unknown as T;
  }

  return schema;
};

const detectUnknownFields = (
  schema: z.ZodObject<z.ZodRawShape>,
  data: unknown,
  endpoint: string
) => {
  if (typeof data !== "object" || data === null) {
    return;
  }

  // Get all defined keys in the schema
  const schemaKeys = new Set(Object.keys(schema.shape));

  // Find keys in response that are not in schema
  const dataRecord = data as Record<string, unknown>;
  const unknownKeys = Object.keys(dataRecord).filter(key => !schemaKeys.has(key));

  if (unknownKeys.length > 0) {
    console.error(`[Zod Passthrough Warning] Unknown fields detected in API response:`, {
      endpoint,
      unknownFields: unknownKeys,
      values: unknownKeys.reduce(
        (acc, key) => {
          acc[key] = dataRecord[key];
          return acc;
        },
        {} as Record<string, unknown>
      ),
    });
  }

  // Recursively check nested objects
  Object.entries(schema.shape).forEach(([key, fieldSchema]) => {
    const fieldData = dataRecord[key];
    if (fieldData === undefined || fieldData === null) {
      return;
    }

    // Unwrap ZodOptional, ZodNullable, ZodDefault to get the inner schema
    let unwrappedSchema = fieldSchema as ZodTypeAny;
    while (
      unwrappedSchema._def.typeName === "ZodOptional" ||
      unwrappedSchema._def.typeName === "ZodNullable" ||
      unwrappedSchema._def.typeName === "ZodDefault"
    ) {
      unwrappedSchema = unwrappedSchema._def.innerType || unwrappedSchema._def.type;
    }

    // Check nested objects
    if (unwrappedSchema instanceof ZodObject) {
      detectUnknownFields(unwrappedSchema, fieldData, `${endpoint}.${key}`);
    }
    // Check arrays of objects
    else if (unwrappedSchema instanceof ZodArray) {
      const arrayItemSchema = unwrappedSchema._def.type;
      if (arrayItemSchema instanceof ZodObject && Array.isArray(fieldData)) {
        fieldData.forEach((item: unknown, index: number) => {
          detectUnknownFields(arrayItemSchema, item, `${endpoint}.${key}[${index}]`);
        });
      }
    }
    // Check union types
    else if (unwrappedSchema instanceof ZodUnion) {
      // For unions, try to find a matching object schema
      const objectSchemas = unwrappedSchema._def.options.filter(
        (option: ZodTypeAny) => option instanceof ZodObject
      );
      if (objectSchemas.length > 0 && typeof fieldData === "object") {
        // Check against the first matching object schema
        // (ideally we'd check which variant matches, but this is a reasonable approximation)
        detectUnknownFields(objectSchemas[0], fieldData, `${endpoint}.${key}`);
      }
    }
  });
};

const patchWithZodSchema = <R>(
  response: unknown,
  endpoint: string,
  zodSchema: AcceptedZodSchema
) => {
  if (!zodSchema) return response as R;
  try {
    const validationResult = zodSchema.safeParse(response);
    if (validationResult.success) {
      // Detect unknown fields if schema is a ZodObject with passthrough
      if (zodSchema instanceof ZodObject) {
        detectUnknownFields(zodSchema, response, endpoint);
      } else if (zodSchema instanceof ZodArray) {
        // For arrays, check each item if the array schema is an object
        const arrayItemSchema = zodSchema._def.type;
        if (arrayItemSchema instanceof ZodObject && Array.isArray(response)) {
          response.forEach((item: unknown, index: number) => {
            detectUnknownFields(arrayItemSchema, item, `${endpoint}[${index}]`);
          });
        }
      }

      return validationResult.data as R;
    }

    // Log to console
    console.error("zod validation error", endpoint, validationResult.error, response);

    // Capture validation error in Sentry
    Sentry.captureException(new Error("Zod validation error"), {
      level: "error",
      tags: {
        type: "zod_validation",
        endpoint: endpoint,
      },
      extra: {
        endpoint: endpoint,
        validationErrors: validationResult.error.errors,
        response: response,
        zodErrorMessage: validationResult.error.message,
      },
    });

    const formattedResponse = applyCatchToSchema(zodSchema).parse(response) as R;
    return formattedResponse;
  } catch (e) {
    console.error("patchWithZodSchema", endpoint, e, response);

    // Capture schema patching error in Sentry
    Sentry.captureException(e instanceof Error ? e : new Error(String(e)), {
      level: "error",
      tags: {
        type: "zod_schema_patch_error",
        endpoint: endpoint,
      },
      extra: {
        endpoint: endpoint,
        response: response,
        error: e,
      },
    });

    return response as R;
  }
};

class APIClient {
  controller?: AbortController;
  /**
   * Fetches data from given url
   */

  constructor() {
    if (!AbortSignal.timeout) {
      AbortSignal.timeout = (ms: number) => {
        const controller = new AbortController();
        setTimeout(() => controller.abort(), ms);
        return controller.signal;
      };
    }
  }

  get = async <R>(
    url: string,
    params: unknown,
    timeout = 0,
    zodSchema: AcceptedZodSchema,
    config?: AxiosRequestConfig
  ) => {
    // Track duplicate API calls
    duplicateCallTracker.trackCall(url, "GET", params);

    if (!checkSecureConnection()) {
      return;
    }
    this.controller = new AbortController();
    const defaultConfig: AxiosRequestConfig = {
      signal: this.controller.signal,
    };
    if (timeout) {
      defaultConfig.signal = AbortSignal.timeout(timeout);
    }
    const finalConfig: AxiosRequestConfig = {
      ...defaultConfig,
      ...config,
      headers: {
        ...axios.defaults.headers.common, // Include global headers
        ...(config?.headers || {}), // Overwrite with explicitly passed headers
      },
    };
    if (!params || !Object.keys(params).length) return axios.get(`${url}`, finalConfig) as R;
    const paramKeys = [];
    for (const k in params) {
      const v = (params as Record<string, string | number | boolean>)[k];
      // Skip undefined and null values
      if (v === undefined || v === null) {
        continue;
      }
      if (!Array.isArray(v)) {
        paramKeys.push(`${k}=${encodeURIComponent(v)}`);
        continue;
      }
      for (const i of v) {
        paramKeys.push(`${k}=${encodeURIComponent(i)}`);
      }
    }

    return patchWithZodSchema<R>(
      await axios.get<R>(`${url}?${paramKeys.join("&")}`, {
        ...params,
        ...finalConfig,
        signal: this.controller.signal,
      }),
      url,
      zodSchema
    );
  };
  /**
   * post given data to url
   */
  post = async <R, D = unknown>(
    url: string,
    data: D,
    zodSchema: AcceptedZodSchema,
    config?: AxiosRequestConfig
  ): Promise<R | undefined> => {
    if (!checkSecureConnection()) {
      return;
    }
    this.controller = new AbortController();
    return patchWithZodSchema<R>(
      await axios.post(url, data, {
        ...config,
        signal: this.controller.signal,
      }),
      url,
      zodSchema
    );
  };

  postWithParams = async <R, P = Record<string, unknown>, D = Record<string, unknown>>(
    url: string,
    params: P,
    data: D,
    zodSchema: AcceptedZodSchema
  ) => {
    if (!checkSecureConnection()) {
      return;
    }
    this.controller = new AbortController();
    return patchWithZodSchema<R>(
      await axios.post(url, data, { params, signal: this.controller.signal }),
      url,
      zodSchema
    );
  };
  /**
   * Updates data
   */
  update = async <R, D = Record<string, unknown>>(
    url: string,
    data: D,
    zodSchema: AcceptedZodSchema
  ) => {
    if (!checkSecureConnection()) {
      return;
    }
    return patchWithZodSchema<R>(
      await axios.patch(url, data, { signal: this.controller?.signal }),
      url,
      zodSchema
    );
  };

  put = async <R, D = Record<string, unknown>>(
    url: string,
    data: D,
    zodSchema: AcceptedZodSchema
  ) => {
    if (!checkSecureConnection()) {
      return;
    }
    return patchWithZodSchema<R>(
      await axios.put(url, data, { signal: this.controller?.signal }),
      url,
      zodSchema
    );
  };
  /**
   * Delete
   */
  delete = async <R>(
    url: string,
    config: Record<string, unknown>,
    zodSchema: AcceptedZodSchema
  ) => {
    if (!checkSecureConnection()) {
      return;
    }
    return patchWithZodSchema<R>(
      await axios.delete(url, { ...config, signal: this.controller?.signal }),
      url,
      zodSchema
    );
  };

  cancel = () => {
    this.controller?.abort();
  };
}

async function* getIterableStream(body: ReadableStream<Uint8Array>) {
  const reader = body.getReader();
  const decoder = new TextDecoder();

  while (true) {
    const { value, done } = await reader.read();
    if (done) {
      break;
    }
    const decodedChunk = decoder.decode(value, { stream: true });
    yield decodedChunk;
  }
}

const generateStream = async (
  url: string,
  onProgress: (chunk: string) => void,
  requestBody: Record<string, unknown> | null = null,
  abortController: AbortController | undefined = undefined
) => {
  const requestData: {
    method: "GET" | "POST";
    headers: Record<string, string>;
    body?: string;
    signal?: AbortSignal;
  } = { method: "GET", headers: streamHeaders };
  if (requestBody) {
    requestData.method = "POST";
    requestData.headers["Content-Type"] = "application/json";
    requestData.body = JSON.stringify(requestBody);
  }

  if (abortController) {
    requestData.signal = abortController.signal;
  }

  const response = await fetch(API_URL + url, requestData);
  if (response.status !== 200) throw new Error(response.status.toString());
  if (!response.body) throw new Error("Response body does not exist");

  const stream = await getIterableStream(response.body);
  for await (const chunk of stream) {
    onProgress(chunk);
  }
};

export {
  axios,
  APIClient,
  setAuthorizationHeader,
  setUserGroupHeader,
  setTenantHeader,
  getIterableStream,
  generateStream,
  duplicateCallTracker,
  streamHeaders,
};

export const api = new APIClient();
